import React, { useEffect, useState } from 'react';
import { fetchDatasetVersions } from '../../services/datasetService';
import { endpoints } from '../../api/endpoints';
import { FaFileExcel, FaFileCsv, FaImages, FaHistory } from 'react-icons/fa';
import { Modal } from 'react-bootstrap';

const DatasetVersionsList = ({ show, onHide }) => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show && versions.length === 0) {
            const loadVersions = async () => {
                setLoading(true);
                try {
                    const data = await fetchDatasetVersions();
                    setVersions(data);
                } catch (err) {
                    console.error("Failed to load versions", err);
                } finally {
                    setLoading(false);
                }
            };
            loadVersions();
        }
    }, [show, versions.length]);

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered className="scientific-modal">
            <Modal.Header closeButton className="border-0 pb-0 ps-4 pt-4">
                <Modal.Title className="text-navy fw-bold d-flex align-items-center">
                    <FaHistory className="me-2" /> Release Archive
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <p className="text-muted mb-4">
                    Access published snapshots of the dataset. Historical versions are preserved for reproducibility in scientific research.
                </p>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-navy" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : versions.length === 0 ? (
                    <div className="alert alert-light border text-center py-4">No historical versions found.</div>
                ) : (
                    <div className="table-responsive rounded-3 border archive-scroll-container">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light text-secondary small text-uppercase">
                                <tr>
                                    <th className="py-3 ps-4 border-0">Version</th>
                                    <th className="py-3 border-0">Release Date</th>
                                    <th className="py-3 border-0">Records</th>
                                    <th className="py-3 border-0">DOI</th>
                                    <th className="py-3 border-0">License</th>
                                    <th className="py-3 border-0">Description</th>
                                    <th className="py-3 text-end pe-4 border-0">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {versions.map((version, index) => (
                                    <tr key={version.id}>
                                        <td className="ps-4 fw-bold text-navy">
                                            <span className="mono-font">{version.versionIdentifier}</span>
                                            {index === 0 && <span className="badge bg-success ms-2" style={{ fontSize: '0.6rem' }}>LATEST</span>}
                                        </td>
                                        <td className="text-muted small">
                                            {formatDate(version.createdAt)}
                                        </td>
                                        <td className="text-muted small">{version.recordCount}</td>
                                        <td className="text-muted small">
                                            {version.doi ? (
                                                <a href={`https://doi.org/${version.doi}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-navy fw-semibold">
                                                    {version.doi}
                                                </a>
                                            ) : (
                                                <span className="text-muted opacity-75">—</span>
                                            )}
                                        </td>
                                        <td className="text-muted small">
                                            {version.license ? (
                                                <span className="badge bg-light text-dark border">{version.license}</span>
                                            ) : (
                                                <span className="text-muted opacity-75">—</span>
                                            )}
                                        </td>
                                        <td className="text-muted small" style={{ maxWidth: '240px' }}>
                                            {version.description ? (
                                                <span title={version.description} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {version.description}
                                                </span>
                                            ) : (
                                                <span className="text-muted opacity-75">—</span>
                                            )}
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="d-flex justify-content-end gap-2">
                                                <a
                                                    href={endpoints.downloadVersionExcel(version.id)}
                                                    className="btn btn-sm btn-success-soft"
                                                    title="Download Excel"
                                                >
                                                    <FaFileExcel className="me-1" /> Excel
                                                </a>
                                                <a
                                                    href={endpoints.downloadVersionCsv(version.id)}
                                                    className="btn btn-sm btn-success-soft"
                                                    title="Download the runs as CSV"
                                                >
                                                    <FaFileCsv className="me-1" /> CSV
                                                </a>
                                                <a
                                                    href={endpoints.downloadVersionImages(version.id)}
                                                    className="btn btn-sm btn-primary-soft"
                                                    title="Download Image Zip"
                                                >
                                                    <FaImages className="me-1" /> Images
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <button className="btn btn-link text-muted btn-sm text-decoration-none" onClick={onHide}>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default DatasetVersionsList;
